import { AlertCircle, KeyRound, Mail, ShieldCheck } from 'lucide-react'
import * as React from 'react'
import { useCountdown } from '../../hooks/useCountdown'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'
import { Icon } from '../atoms/Icon'

/**
 * AccountLockedScreen — purpose-built blocking screen for HTTP 423
 * ACCOUNT_LOCKED responses (BE error code 2007).
 *
 * Composition (no new visual primitives):
 *
 *   - Outer landmark with role="alert" + aria-live="polite" so screen
 *     readers announce the lock on render.
 *   - Heading + Alert (tone="error") carrying the BE-supplied
 *     user_message verbatim (no client-side rewording).
 *   - Live countdown when lockedUntil is in the future. Falls through
 *     to "You can try again now" + a Retry button once it expires.
 *   - One Button per entry in unlockOptions, EXCLUDING "wait" (covered
 *     by the countdown). Sort order is canonical: password_reset ->
 *     magic_link_unlock -> trusted_device.
 *
 * Wire shape (BE-shipped):
 *
 *   {
 *     code: "ACCOUNT_LOCKED",
 *     locked_until?: ISO 8601 | null,    // nil = admin-only lock (no auto-unlock)
 *     unlock_options: ("wait"|"password_reset"|"magic_link_unlock"|"trusted_device")[]
 *   }
 *
 * Consumers (LoginForm, RecoveryPage, SecurityTab in dp-auth-ui;
 * LoginPage, ChangePasswordPage in dp-admin-console) render this in
 * place of their form when err.code === 'ACCOUNT_LOCKED'.
 */

export type AccountUnlockOption = 'wait' | 'password_reset' | 'magic_link_unlock' | 'trusted_device'

export interface AccountLockedScreenProps {
  /** End-user-safe copy from `error.user_message`. Rendered verbatim. */
  userMessage: string
  /**
   * ISO 8601 lock-expiry timestamp. When set, drives the live countdown.
   * When nullish, the screen renders the "contact your administrator"
   * copy with no countdown.
   */
  lockedUntil?: string | null
  /**
   * Per-tenant filtered list of self-service unlock paths. Each entry
   * (other than "wait") renders as a Button that calls onAction with
   * the slug.
   */
  unlockOptions: string[]
  /**
   * Fired when the user picks an unlock option. The "wait" option does
   * not invoke this; "retry" fires once the countdown expires and the
   * user clicks the rendered Retry button.
   */
  onAction?: (action: AccountUnlockOption | 'retry') => void
  className?: string
}

// Canonical sort + display map for the unlock options. Sorted in the
// order users should see them (most-actionable to least), independent
// of the order the BE returned them. The "wait" option deliberately
// has no entry — it is rendered via the countdown, never as a button.
type ActionableUnlockOption = Exclude<AccountUnlockOption, 'wait'>

const UNLOCK_OPTION_ORDER: ActionableUnlockOption[] = [
  'password_reset',
  'magic_link_unlock',
  'trusted_device',
]

const UNLOCK_OPTION_META: Record<
  ActionableUnlockOption,
  { label: string; icon: typeof KeyRound; variant: 'primary' | 'secondary' | 'outline' }
> = {
  password_reset: { label: 'Reset password', icon: KeyRound, variant: 'primary' },
  magic_link_unlock: { label: 'Email me an unlock link', icon: Mail, variant: 'secondary' },
  trusted_device: { label: 'Use trusted device', icon: ShieldCheck, variant: 'outline' },
}

function isActionableOption(value: string): value is ActionableUnlockOption {
  return value === 'password_reset' || value === 'magic_link_unlock' || value === 'trusted_device'
}

export const AccountLockedScreen = React.forwardRef<HTMLElement, AccountLockedScreenProps>(
  ({ userMessage, lockedUntil, unlockOptions, onAction, className }, ref) => {
    const { totalSeconds, formatted, expired } = useCountdown(lockedUntil ?? null)
    const hasCountdown = Boolean(lockedUntil)

    // Defensive fallback for empty-string user_message. The BE spec
    // guarantees a non-empty user_message, but `?? fallback` in consumer
    // code does NOT catch the empty-string case (only nullish). One-line
    // guard here means the body of the alert panel is never blank.
    const safeUserMessage = userMessage || 'Your account is locked.'

    // De-duplicate + canonical-sort the actionable options. The BE is the
    // source of truth on WHICH options apply per tenant; the FE just owns
    // the display order so the same lock surface looks identical across
    // consumers regardless of BE response ordering.
    const sortedActions = React.useMemo(() => {
      const present = new Set(unlockOptions)
      return UNLOCK_OPTION_ORDER.filter((slug) => present.has(slug) && isActionableOption(slug))
    }, [unlockOptions])

    // Minute-bucket text for the screen-reader live region. The visible
    // countdown ticks every second for sighted users; we deliberately
    // restrict the announceable text to minute-grain changes so AT users
    // are not bombarded with re-announcements ("29 min 59 sec ->
    // 29 min 58 sec -> ..." every second). Math.ceil so "1 minute" is
    // reached at the 60s -> 59s boundary, matching how the user intuits
    // "how long do I wait".
    const minutesRemaining = Math.ceil(totalSeconds / 60)
    const minuteBucketText = React.useMemo(() => {
      if (!hasCountdown || expired) return ''
      if (totalSeconds <= 0) return ''
      if (totalSeconds < 60) return 'Try again in less than a minute.'
      if (minutesRemaining === 1) return 'Try again in 1 minute.'
      return `Try again in ${minutesRemaining} minutes.`
    }, [hasCountdown, expired, totalSeconds, minutesRemaining])

    return (
      <section
        ref={ref}
        role="alert"
        aria-live="polite"
        aria-atomic="true"
        className={cn('flex flex-col gap-5 text-left', className)}
      >
        <div className="flex flex-col gap-1">
          <h1 className="text-heading-xl text-ia-text">Account temporarily locked</h1>
          <p className="text-body-sm text-ia-text-secondary">
            For your security, we paused sign-in attempts on this account.
          </p>
        </div>

        {/* user_message panel — mirrors Alert tone="error" styling but
            without the inline role="alert" so the outer landmark is the
            single ARIA-live region. Two role="alert" nodes inside one
            tree cause screen readers to announce the lock twice and
            also fail Testing Library's getByRole('alert') lookups. */}
        <div
          className={cn(
            'rounded-md border px-4 py-3 flex gap-3 items-start',
            'bg-ia-error-bg border-ia-error/20 text-ia-text',
          )}
        >
          <div className="flex-shrink-0 pt-0.5 text-ia-error">
            <Icon icon={AlertCircle} size="md" />
          </div>
          <div className="flex-1">
            <div className="text-body-sm font-semibold mb-1">Sign-in blocked</div>
            <div className="text-body-sm text-ia-text-secondary">{safeUserMessage}</div>
          </div>
        </div>

        {hasCountdown ? (
          <div
            className={cn(
              'rounded-md border border-ia-border bg-ia-elevated/30 px-4 py-3',
              'flex flex-col gap-1',
            )}
          >
            {expired ? (
              <>
                <p className="text-body-md font-medium text-ia-text">You can try again now.</p>
                <p className="text-body-sm text-ia-text-secondary">
                  The lock just expired. Sign in to continue.
                </p>
              </>
            ) : (
              <>
                {/* Visible countdown for sighted users — ticks every
                    second. aria-hidden excludes this subtree from the
                    accessibility tree so per-second DOM mutations do not
                    trigger the outer live region. The polite SR-only
                    sibling below carries the announceable text, snapped
                    to minute boundaries (brief: "announce every minute,
                    not every second — would be noisy"). */}
                <p aria-hidden="true" className="text-body-sm text-ia-text-secondary">
                  Try again in
                </p>
                <p
                  aria-hidden="true"
                  className="text-heading-lg font-semibold text-ia-text tabular-nums"
                >
                  {formatted}
                </p>
                <span className="sr-only" aria-live="polite">
                  {minuteBucketText}
                </span>
              </>
            )}
          </div>
        ) : (
          <div
            className={cn(
              'rounded-md border border-ia-border bg-ia-elevated/30 px-4 py-3',
              'text-body-sm text-ia-text-secondary',
            )}
          >
            Contact your administrator to unlock this account.
          </div>
        )}

        {((hasCountdown && expired) || sortedActions.length > 0) && (
          <div className="flex flex-col gap-2">
            {hasCountdown && expired && (
              // Gate on hasCountdown && expired (not expired alone).
              // useCountdown returns expired=true from its TERMINAL
              // sentinel whenever lockedUntil is nullish (admin-only
              // lock), so the bare `expired` gate would leak a misleading
              // "Try again" affordance into the admin-only state where
              // the brief explicitly says: no retry, contact admin only.
              <Button type="button" variant="primary" onClick={() => onAction?.('retry')}>
                Try again
              </Button>
            )}
            {sortedActions.map((slug) => {
              const meta = UNLOCK_OPTION_META[slug]
              return (
                <Button
                  key={slug}
                  type="button"
                  variant={meta.variant}
                  leftIcon={meta.icon}
                  onClick={() => onAction?.(slug)}
                >
                  {meta.label}
                </Button>
              )
            })}
          </div>
        )}
      </section>
    )
  },
)
AccountLockedScreen.displayName = 'AccountLockedScreen'
